import React, { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import { Select, Radio, Card, Switch, InputNumber, Button, Modal, Drawer, Segmented, Divider, Typography } from 'antd';
import { FullscreenOutlined } from '@ant-design/icons';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  ComposedChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceArea,
  ReferenceLine,
  Label,
} from 'recharts';
import { useTheme } from '../../contexts/useTheme';
import { useChartAnimations } from '../../contexts/useChartAnimations';
import { useEventSelection } from '../../contexts/useEventSelection';
import type { ForecastDaily, ForecastHourly, HourlyForecastParameter } from '../../api/forecastService';
import type { ForecastField } from '../../api/forecastFieldsService';
import { getParameterValue } from '../../utils/weatherUtils';
import { parseWindValue, parsePercentageValue } from './fieldRenderers';

type ChartType = 'line' | 'bar';

export interface ForecastChartDisplaySettings {
  selectedFields: string[];
  chartType: ChartType;
  useDualYAxis: boolean;
  showLegend: boolean;
  yAxisSide: 'left' | 'right';
  yAxisScale: 'auto' | 'custom';
  yAxisStartValue: number;
  yAxisEndValue: number | null;
}

export type ForecastChartWidgetMode = 'auto' | 'daily' | 'hourly';

export interface ForecastChartWidgetConfig {
  forecastMode: ForecastChartWidgetMode;
  filterBySession: boolean;
  hoursBeforeSession: number;
  activeForecastType: 'daily' | 'hourly';
}

const ONE_HOUR_MS = 60 * 60 * 1000;

/** Keys on chart rows that must never appear in the tooltip */
const TOOLTIP_INTERNAL_DATA_KEYS = new Set(['collapsedX', 'collapsedHourOrdinal', 'windArrowY']);

interface ForecastChartViewProps {
  forecasts: ForecastDaily[] | ForecastHourly[];
  selectedDate: Date | null;
  forecastType: 'daily' | 'hourly';
  availableFields: ForecastField[];
  // Session filtering props (only used for hourly forecasts)
  filterBySession?: boolean;
  hoursBeforeSession?: number;
  sessions?: Array<{ start_time: string; end_time: string; session_name: string }>;
  /** When 'compact', hides inline controls and uses a settings drawer */
  variant?: 'full' | 'compact';
  /** Hide the fullscreen button (e.g. dashboard widget embed) */
  hideFullScreen?: boolean;
  /** Parent can open settings/fullscreen drawers (dashboard widget gear icon) */
  compactActionsRef?: React.RefObject<{
    openSettings: () => void;
    openFullScreen: () => void;
  } | null>;
  /** Controlled display settings — skips localStorage when onSettingsChange is provided */
  settings?: Partial<ForecastChartDisplaySettings>;
  onSettingsChange?: (settings: ForecastChartDisplaySettings) => void;
  /** Widget-level settings shown in compact drawer */
  widgetConfig?: ForecastChartWidgetConfig;
  onWidgetConfigChange?: (partial: Partial<Omit<ForecastChartWidgetConfig, 'activeForecastType'>>) => void;
  /** Override empty-state message (compact widget embed) */
  emptyMessage?: string;
  /** Force-disable chart animations regardless of global setting. Useful in resizable containers. */
  disableAnimations?: boolean;
  /** When true, strips inner card chrome for dashboard widget embedding */
  embeddedInWidget?: boolean;
}

interface ChartDataPoint {
  timestamp: string;
  timestampMs?: number; // Numeric timestamp for continuous x-axis (hourly chart)
  dateKey?: string; // YYYY-MM-DD for date-only daily forecasts
  /** Sequential index 0..n-1 when Sessions Only is on — hour slot for session shading math */
  collapsedHourOrdinal?: number;
  /** Plot position on collapsed axis — equals hourOrdinal so labels, session bands, and NOW line align */
  collapsedX?: number;
  time: string;
  date: string;
  dateTimeLabel: string; // Combined label for x-axis display (date above, time below)
  windArrowY?: number; // Fixed Y position for wind direction arrows
  [key: string]: string | number | null | undefined; // Dynamic field values
}

const getDateKeyInTimeZone = (date: Date, timeZone: string): string => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);

  const year = parts.find(part => part.type === 'year')?.value;
  const month = parts.find(part => part.type === 'month')?.value;
  const day = parts.find(part => part.type === 'day')?.value;

  return `${year}-${month}-${day}`;
};

const dateOnlyToUtcNoon = (dateValue: string): Date => {
  const [year, month, day] = dateValue.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day, 12));
};

const formatDateOnly = (
  dateValue: string,
  options: Intl.DateTimeFormatOptions,
): string => {
  // forecast_date is already a circuit-local calendar date, not an instant.
  // Format it in UTC so the viewer's browser timezone cannot shift the day.
  return dateOnlyToUtcNoon(dateValue).toLocaleDateString('en-US', {
    ...options,
    timeZone: 'UTC',
  });
};

/** Map session wall-clock interval to x on the collapsed (ordinal) axis using fractional positions within each hour slot */
function sessionIntervalToCollapsedXExtent(
  chartData: ChartDataPoint[],
  sessionStartMs: number,
  sessionEndMs: number,
): [number, number] | null {
  let xMin = Infinity;
  let xMax = -Infinity;
  chartData.forEach((pt) => {
    const i = pt.collapsedHourOrdinal ?? 0;
    const hourStart = (pt.timestampMs ?? new Date(pt.timestamp).getTime()) as number;
    const hourEnd = hourStart + ONE_HOUR_MS;
    const overlapStart = Math.max(sessionStartMs, hourStart);
    const overlapEnd = Math.min(sessionEndMs, hourEnd);
    if (overlapStart < overlapEnd) {
      const x1 = i + (overlapStart - hourStart) / ONE_HOUR_MS;
      const x2 = i + (overlapEnd - hourStart) / ONE_HOUR_MS;
      xMin = Math.min(xMin, x1);
      xMax = Math.max(xMax, x2);
    }
  });
  if (xMin === Infinity) return null;
  return [xMin, xMax];
}

// Color palette for fields (distinct colors)
const FIELD_COLORS = [
  '#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd',
  '#8c564b', '#e377c2', '#7f7f7f', '#bcbd22', '#17becf',
];

// Wind speed color scale (matches StationCharts)
const windSpeedColors = [
  { max: 5, color: '#00CED1', label: '< 5 km/h' },      // Cyan
  { max: 10, color: '#32CD32', label: '5-10 km/h' },    // Green
  { max: 15, color: '#FFD700', label: '10-15 km/h' },   // Yellow
  { max: 20, color: '#FFA500', label: '15-20 km/h' },   // Orange
  { max: Infinity, color: '#FF4444', label: '> 20 km/h' }, // Red
];

const getWindSpeedColor = (speed: number | null | undefined): string => {
  if (speed === null || speed === undefined) return '#666';
  for (const { max, color } of windSpeedColors) {
    if (speed < max) return color;
  }
  return '#FF4444';
};

// Custom arrow shape for wind direction markers
interface WindArrowProps {
  cx?: number;
  cy?: number;
  payload?: ChartDataPoint;
  fill?: string;
}

const WindArrow: React.FC<WindArrowProps> = ({ cx, cy, payload }) => {
  if (!cx || !cy || !payload?.wind_direction) return null;
  
  // Wind direction is where the wind is coming FROM, so we add 180° to show where it's going
  // SVG rotation: 0° = pointing right, so we adjust: North (0°) should point up
  const rotation = ((payload.wind_direction as number) + 180) % 360;
  
  // Color based on wind speed (use wind_speed if available, otherwise default)
  const windSpeed = payload.wind_speed as number | null | undefined;
  const arrowColor = getWindSpeedColor(windSpeed);
  
  return (
    <g transform={`translate(${cx}, ${cy}) rotate(${rotation})`}>
      <path
        d="M0,-9 L4.5,6 L0,3 L-4.5,6 Z"
        fill={arrowColor}
        stroke="#333"
        strokeWidth={0.5}
      />
    </g>
  );
};

// Custom tick component for multi-line x-axis labels (date above, time below)
const CustomXAxisTick = ({
  x,
  y,
  payload,
  textColor,
  timezone = 'UTC',
  fontSize = 11,
}: {
  x: string | number;
  y: string | number;
  payload: { value: string | number };
  textColor: string;
  timezone?: string;
  fontSize?: number;
}) => {
  const value = payload.value;
  const label = typeof value === 'number'
    ? `${new Date(value).toLocaleDateString('en-US', { month: 'short', day: '2-digit', timeZone: timezone })}\n${new Date(value).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: timezone })}`
    : value;
  const [date, time] = String(label).split('\n');
  const tickX = Number(x);
  const tickY = Number(y);
  const lineHeight = Math.round(fontSize * 1.25);
  return (
    <g transform={`translate(${tickX},${tickY + 8})`}>
      <text x={0} y={0} textAnchor="middle" fill={textColor} fontSize={fontSize}>
        <tspan x={0} dy={0}>{date}</tspan>
        <tspan x={0} dy={lineHeight}>{time}</tspan>
      </text>
    </g>
  );
};

export const ForecastChartView: React.FC<ForecastChartViewProps> = ({
  forecasts,
  selectedDate,
  forecastType,
  availableFields,
  filterBySession = false,
  hoursBeforeSession = 2,
  sessions = [],
  variant = 'full',
  hideFullScreen = false,
  compactActionsRef,
  settings: controlledSettings,
  onSettingsChange,
  widgetConfig,
  onWidgetConfigChange,
  emptyMessage,
  disableAnimations = false,
  embeddedInWidget = false,
}) => {
  const { theme } = useTheme();
  const { chartAnimationsEnabled: globalAnimations } = useChartAnimations();
  const chartAnimationsEnabled = disableAnimations ? false : globalAnimations;
  const { selectedEvent } = useEventSelection();
  const isDark = theme === 'dark';
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const [currentTime, setCurrentTime] = useState<Date>(new Date());
  const [isSettingsDrawerOpen, setIsSettingsDrawerOpen] = useState(false);
  const isControlled = !!onSettingsChange;

  // Update current time every minute (aligned to minute boundaries)
  useEffect(() => {
    const now = new Date();
    const msUntilNextMinute = (60 - now.getSeconds()) * 1000 - now.getMilliseconds();

    const alignTimeout = setTimeout(() => {
      setCurrentTime(new Date());
      const interval = setInterval(() => {
        setCurrentTime(new Date());
      }, 60 * 1000);

      return () => clearInterval(interval);
    }, msUntilNextMinute);

    return () => clearTimeout(alignTimeout);
  }, []);
  
  // localStorage keys for persisting chart preferences
  const fieldsStorageKey = `${forecastType}ForecastChartFields`;
  const chartTypeStorageKey = `${forecastType}ForecastChartType`;
  
  // Load saved preferences from localStorage (uncontrolled) or parent config (controlled)
  const loadSavedPreferences = () => {
    if (controlledSettings) {
      return {
        fields: controlledSettings.selectedFields ?? [],
        chartType: (controlledSettings.chartType as ChartType) ?? 'line',
      };
    }
    try {
      const savedFields = localStorage.getItem(fieldsStorageKey);
      const savedChartType = localStorage.getItem(chartTypeStorageKey);
      return {
        fields: savedFields ? JSON.parse(savedFields) : [],
        chartType: (savedChartType as ChartType) || 'line',
      };
    } catch (error) {
      console.error('Error loading chart preferences from localStorage:', error);
      return { fields: [], chartType: 'line' as ChartType };
    }
  };
  
  const savedPreferences = loadSavedPreferences();
  const [selectedFields, setSelectedFields] = useState<string[]>(savedPreferences.fields);
  const [chartType, setChartType] = useState<ChartType>(savedPreferences.chartType);
  const [useDualYAxis, setUseDualYAxis] = useState<boolean>(controlledSettings?.useDualYAxis ?? false);
  const [showLegend, setShowLegend] = useState<boolean>(controlledSettings?.showLegend ?? true);
  /** When using a single Y-axis, render it on the left or right (dual mode fixes left/right per group). */
  const [yAxisSide, setYAxisSide] = useState<'left' | 'right'>(controlledSettings?.yAxisSide ?? 'left');
  const [yAxisScale, setYAxisScale] = useState<'auto' | 'custom'>(controlledSettings?.yAxisScale ?? 'auto');
  const [yAxisStartValue, setYAxisStartValue] = useState<number>(controlledSettings?.yAxisStartValue ?? 0);
  const [yAxisEndValue, setYAxisEndValue] = useState<number | null>(controlledSettings?.yAxisEndValue ?? null);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [fullscreenChartHeight, setFullscreenChartHeight] = useState(500);
  const [chartHeight, setChartHeight] = useState(0);
  const minChartHeight = variant === 'compact' ? 120 : 300;
  const timezone = selectedEvent?.circuit?.time_zone || 'UTC';

  useEffect(() => {
    if (variant === 'compact' && compactActionsRef) {
      compactActionsRef.current = {
        openSettings: () => setIsSettingsDrawerOpen(true),
        openFullScreen: () => setIsFullScreen(true),
      };
      return () => {
        compactActionsRef.current = null;
      };
    }
  }, [variant, compactActionsRef]);

  useEffect(() => {
    if (!isFullScreen) return;
    const updateHeight = () => setFullscreenChartHeight(window.innerHeight - 220);
    updateHeight();
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, [isFullScreen]);

  /** Equal spacing per retained hour (matches card/table ordering); avoids empty calendar gaps on the x-axis */
  const useCollapsedSessionAxis =
    forecastType === 'hourly' && filterBySession && sessions.length > 0;
  
  // Check if a given datetime falls within any session window (including hours before)
  const isWithinSessionWindow = useCallback((datetime: Date): boolean => {
    if (!filterBySession || sessions.length === 0) return true;

    const hourStart = datetime.getTime();
    const hourEnd = hourStart + ONE_HOUR_MS;
    const hoursBeforeMs = hoursBeforeSession * 60 * 60 * 1000;

    return sessions.some(session => {
      const sessionStart = new Date(session.start_time).getTime();
      const sessionEnd = new Date(session.end_time).getTime();
      const windowStart = sessionStart - hoursBeforeMs;
      const windowEnd = sessionEnd + ONE_HOUR_MS;
      return hourStart < windowEnd && hourEnd > windowStart;
    });
  }, [filterBySession, sessions, hoursBeforeSession]);
  
  // Save preferences to localStorage when they change (uncontrolled mode only)
  useEffect(() => {
    if (isControlled) return;
    try {
      localStorage.setItem(fieldsStorageKey, JSON.stringify(selectedFields));
    } catch (error) {
      console.error('Error saving chart fields to localStorage:', error);
    }
  }, [selectedFields, fieldsStorageKey, isControlled]);
  
  useEffect(() => {
    if (isControlled) return;
    try {
      localStorage.setItem(chartTypeStorageKey, chartType);
    } catch (error) {
      console.error('Error saving chart type to localStorage:', error);
    }
  }, [chartType, chartTypeStorageKey, isControlled]);

  const settingsChangeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const skipInitialSettingsReportRef = useRef(true);
  useEffect(() => {
    if (!onSettingsChange) return;

    if (skipInitialSettingsReportRef.current) {
      skipInitialSettingsReportRef.current = false;
      return;
    }

    if (settingsChangeTimerRef.current) clearTimeout(settingsChangeTimerRef.current);
    settingsChangeTimerRef.current = setTimeout(() => {
      onSettingsChange({
        selectedFields,
        chartType,
        useDualYAxis,
        showLegend,
        yAxisSide,
        yAxisScale,
        yAxisStartValue,
        yAxisEndValue,
      });
    }, 300);

    return () => {
      if (settingsChangeTimerRef.current) clearTimeout(settingsChangeTimerRef.current);
    };
  }, [
    onSettingsChange,
    selectedFields,
    chartType,
    useDualYAxis,
    showLegend,
    yAxisSide,
    yAxisScale,
    yAxisStartValue,
    yAxisEndValue,
  ]);

  // Get valid forecasts issued before selected time
  const validForecasts = useMemo(() => {
    if (!forecasts || forecasts.length === 0) return [];

    if (!selectedDate) {
      return forecasts;
    }

    const selectedTime = selectedDate.getTime();
    return forecasts.filter(f => {
      const issueTime = new Date(f.value_date).getTime();
      return issueTime <= selectedTime;
    });
  }, [forecasts, selectedDate]);

  // Transform forecast data into chart-ready format
  const chartData = useMemo<ChartDataPoint[]>(() => {
    if (validForecasts.length === 0 || selectedFields.length === 0) return [];

    const dataPoints: ChartDataPoint[] = [];

    if (forecastType === 'hourly') {
      const hourlyForecasts = validForecasts as ForecastHourly[];
      
      // Group by hour and create data points - use most recent forecast for each hour
      const hourGroups = new Map<string, { forecast: ForecastHourly; parameters: HourlyForecastParameter[] }>();
      
      hourlyForecasts.forEach(forecast => {
        if (!forecast.hourly_forecasts) return;
        
        forecast.hourly_forecasts.forEach(hf => {
          const datetimeValue = typeof hf.forecast_datetime === 'object' && hf.forecast_datetime !== null && 'value' in hf.forecast_datetime
            ? (hf.forecast_datetime as { value: string }).value
            : hf.forecast_datetime;
          const datetime = new Date(datetimeValue);
          
          // Filter out hours that have fully passed (keep the current hour visible)
          if (selectedDate && datetime.getTime() + 3600000 <= selectedDate.getTime()) {
            return;
          }
          
          // Filter by session window if enabled
          if (!isWithinSessionWindow(datetime)) {
            return;
          }
          
          const hourKey = datetime.toISOString();
          const existing = hourGroups.get(hourKey);
          if (!existing || new Date(forecast.value_date).getTime() > new Date(existing.forecast.value_date).getTime()) {
            hourGroups.set(hourKey, { forecast, parameters: hf.parameters });
          }
        });
      });

      // Convert to sorted array
      const sortedHours = Array.from(hourGroups.entries())
        .sort((a, b) => new Date(a[0]).getTime() - new Date(b[0]).getTime());

      sortedHours.forEach(([timestamp, { parameters }]) => {
        const datetime = new Date(timestamp);
        const timeStr = datetime.toLocaleTimeString('en-US', {
          timeZone: timezone,
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        });
        const dateStr = datetime.toLocaleDateString('en-US', {
          timeZone: timezone,
          month: 'short',
          day: '2-digit',
        });
        const dataPoint: ChartDataPoint = {
          timestamp,
          timestampMs: datetime.getTime(),
          time: timeStr,
          date: dateStr,
          dateTimeLabel: `${dateStr}\n${timeStr}`, // Combined label with newline for multi-line display
        };

        // Extract values for selected fields
        selectedFields.forEach(fieldDbName => {
          const param = getParameterValue(parameters, fieldDbName);
          let value: number | null = null;
          
          if (param.value !== null && param.value !== undefined && param.value !== '') {
            const field = availableFields.find(f => f.db_name === fieldDbName);
            const needsWindConversion = fieldDbName.includes('wind_speed') || fieldDbName.includes('wind_gust');
            
            if (needsWindConversion) {
              value = parseWindValue(param.value);
            } else if (field?.unit === '%' || fieldDbName.includes('probability')) {
              value = parsePercentageValue(param.value);
            } else {
              const numValue = parseFloat(param.value);
              value = !isNaN(numValue) ? numValue : null;
            }
          }
          
          dataPoint[fieldDbName] = value;
        });

        dataPoints.push(dataPoint);
      });

      if (useCollapsedSessionAxis) {
        dataPoints.forEach((pt, hourOrdinal) => {
          pt.collapsedHourOrdinal = hourOrdinal;
          pt.collapsedX = hourOrdinal;
        });
      }
    } else {
      // Daily forecast
      const dailyForecasts = validForecasts as ForecastDaily[];
      
      // Get most recent forecast
      const latestForecast = dailyForecasts.sort((a, b) => 
        new Date(b.value_date).getTime() - new Date(a.value_date).getTime()
      )[0];
      
      if (!latestForecast || !latestForecast.daily_forecasts) return [];

      const selectedDateKey = selectedDate ? getDateKeyInTimeZone(selectedDate, timezone) : null;

      // Filter daily forecasts to selected date onwards
      const filteredDaily = latestForecast.daily_forecasts.filter(df => {
        if (!selectedDateKey) return true;
        
        const dateValue = typeof df.forecast_date === 'object' && df.forecast_date !== null && 'value' in df.forecast_date
          ? (df.forecast_date as { value: string }).value
          : df.forecast_date;
        
        return dateValue >= selectedDateKey;
      });

      filteredDaily.forEach(df => {
        const dateValue = typeof df.forecast_date === 'object' && df.forecast_date !== null && 'value' in df.forecast_date
          ? (df.forecast_date as { value: string }).value
          : df.forecast_date;
        
        const forecastDate = dateOnlyToUtcNoon(dateValue);
        const weekdayStr = formatDateOnly(dateValue, {
          weekday: 'short',
        });
        const dateStr = formatDateOnly(dateValue, {
          month: 'short',
          day: '2-digit',
        });
        
        const dataPoint: ChartDataPoint = {
          timestamp: forecastDate.toISOString(),
          dateKey: dateValue,
          time: weekdayStr,
          date: dateStr,
          dateTimeLabel: `${dateStr}\n${weekdayStr}`, // Combined label with newline for multi-line display
        };

        // Extract values for selected fields
        selectedFields.forEach(fieldDbName => {
          const param = getParameterValue(df.parameters, fieldDbName);
          let value: number | null = null;
          
          if (param.value !== null && param.value !== undefined && param.value !== '') {
            const field = availableFields.find(f => f.db_name === fieldDbName);
            const needsWindConversion = fieldDbName.includes('wind_speed') || fieldDbName.includes('wind_gust');
            
            if (needsWindConversion) {
              value = parseWindValue(param.value);
            } else if (field?.unit === '%' || fieldDbName.includes('probability')) {
              value = parsePercentageValue(param.value);
            } else {
              const numValue = parseFloat(param.value);
              value = !isNaN(numValue) ? numValue : null;
            }
          }
          
          dataPoint[fieldDbName] = value;
        });

        dataPoints.push(dataPoint);
      });
    }

    // Add wind direction arrows if wind_direction is selected
    const hasWindDirection = selectedFields.includes('wind_direction');
    if (hasWindDirection && dataPoints.length > 0) {
      // Calculate interval for wind arrows (show ~20 arrows max to avoid clutter)
      const arrowInterval = Math.max(1, Math.floor(dataPoints.length / 20));
      
      // Add windArrowY for arrow positioning (only at intervals where we want to show arrows)
      // Position arrows at the actual wind_direction value (on the line itself)
      dataPoints.forEach((point, index) => {
        const windDir = point.wind_direction;
        if (index % arrowInterval === 0 && windDir !== null && windDir !== undefined && typeof windDir === 'number') {
          point.windArrowY = windDir;
        }
      });
    }

    return dataPoints;
  }, [validForecasts, selectedFields, forecastType, selectedDate, timezone, availableFields, isWithinSessionWindow, useCollapsedSessionAxis]);

  useEffect(() => {
    const updateHeight = () => {
      if (!chartContainerRef.current) return;
      const rect = chartContainerRef.current.getBoundingClientRect();
      if (rect.height > 0) {
        setChartHeight(Math.max(minChartHeight, rect.height));
      }
    };

    let resizeObserver: ResizeObserver | null = null;

    const attachObserver = () => {
      updateHeight();
      const container = chartContainerRef.current;
      if (!container) return;
      resizeObserver?.disconnect();
      resizeObserver = new ResizeObserver(updateHeight);
      resizeObserver.observe(container);
    };

    const timeoutId = setTimeout(attachObserver, 0);
    window.addEventListener('resize', updateHeight);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', updateHeight);
      resizeObserver?.disconnect();
    };
  }, [variant, minChartHeight, chartData.length, validForecasts.length]);

  const collapsedAxisTicks = useMemo(() => {
    if (!useCollapsedSessionAxis || chartData.length === 0) return undefined;
    const n = chartData.length;
    const maxTicks = 10;
    const xs = chartData.map((pt) => pt.collapsedX as number);
    if (n <= maxTicks) return xs;
    const step = Math.ceil((n - 1) / (maxTicks - 1));
    const ticks: number[] = [];
    for (let i = 0; i < n; i += step) ticks.push(xs[i]);
    if (ticks[ticks.length - 1] !== xs[n - 1]) ticks.push(xs[n - 1]);
    return ticks;
  }, [useCollapsedSessionAxis, chartData]);

  // Calculate field ranges and detect if we need separate y-axes
  const fieldRanges = useMemo(() => {
    if (chartData.length === 0 || selectedFields.length === 0) {
      return [];
    }

    const ranges: Array<{ fieldDbName: string; min: number; max: number; center: number }> = [];
    
    selectedFields.forEach(fieldDbName => {
      const values: number[] = [];
      
      chartData.forEach(point => {
        const value = point[fieldDbName];
        if (typeof value === 'number' && !isNaN(value) && value !== null) {
          values.push(value);
        }
      });

      if (values.length > 0) {
        const minValue = Math.min(...values);
        const maxValue = Math.max(...values);
        ranges.push({ 
          fieldDbName, 
          min: minValue, 
          max: maxValue, 
          center: (minValue + maxValue) / 2 
        });
      }
    });

    return ranges.sort((a, b) => a.center - b.center);
  }, [chartData, selectedFields]);

  // Determine y-axis groups based on user selection (single or dual); mirrors AllStationsComparison
  const yAxisGroups = useMemo(() => {
    if (fieldRanges.length === 0) {
      return [];
    }

    const overallMin = Math.min(...fieldRanges.map(r => r.min));
    const overallMax = Math.max(...fieldRanges.map(r => r.max));
    const overallRange = overallMax - overallMin;
    const padding = overallRange > 0 ? overallRange * 0.05 : Math.max(1, Math.abs(overallMin) * 0.1);

    const applyYAxisScale = (min: number, max: number): [number, number] => {
      const dataTop = Math.ceil(max + padding);
      const dataBottom = Math.floor(min - padding);
      if (yAxisScale === 'custom') {
        const bottom = yAxisStartValue;
        const top = yAxisEndValue ?? dataTop;
        return [bottom, top];
      }
      return [dataBottom, dataTop];
    };

    // Single y-axis: all series share one scale, placed on chosen side
    if (!useDualYAxis || fieldRanges.length === 1) {
      return [{
        yAxisId: yAxisSide,
        fieldDbNames: fieldRanges.map(r => r.fieldDbName),
        domain: applyYAxisScale(overallMin, overallMax),
      }];
    }

    // Dual: multiple params on left (even indices in selection order) and right (odd indices)
    const rangeByField = new Map(fieldRanges.map(r => [r.fieldDbName, r]));
    const lowerGroup: typeof fieldRanges = [];
    const upperGroup: typeof fieldRanges = [];
    selectedFields.forEach((fieldDbName, index) => {
      const range = rangeByField.get(fieldDbName);
      if (range) {
        if (index % 2 === 0) {
          lowerGroup.push(range);
        } else {
          upperGroup.push(range);
        }
      }
    });

    if (lowerGroup.length === 0 || upperGroup.length === 0) {
      return [{
        yAxisId: yAxisSide,
        fieldDbNames: fieldRanges.map(r => r.fieldDbName),
        domain: applyYAxisScale(overallMin, overallMax),
      }];
    }

    const lowerMin = Math.min(...lowerGroup.map(r => r.min));
    const lowerMax = Math.max(...lowerGroup.map(r => r.max));
    const lowerRange = lowerMax - lowerMin;
    const lowerPadding = lowerRange > 0 ? lowerRange * 0.05 : Math.max(1, Math.abs(lowerMin) * 0.1);

    const upperMin = Math.min(...upperGroup.map(r => r.min));
    const upperMax = Math.max(...upperGroup.map(r => r.max));
    const upperRange = upperMax - upperMin;
    const upperPadding = upperRange > 0 ? upperRange * 0.05 : Math.max(1, Math.abs(upperMin) * 0.1);

    const applyLower = (min: number, max: number): [number, number] => {
      const dataTop = Math.ceil(max + lowerPadding);
      const dataBottom = Math.floor(min - lowerPadding);
      if (yAxisScale === 'custom') {
        const bottom = yAxisStartValue;
        const top = yAxisEndValue ?? dataTop;
        return [bottom, top];
      }
      return [dataBottom, dataTop];
    };
    const applyUpper = (min: number, max: number): [number, number] => {
      const dataTop = Math.ceil(max + upperPadding);
      const dataBottom = Math.floor(min - upperPadding);
      if (yAxisScale === 'custom') {
        const bottom = yAxisStartValue;
        const top = yAxisEndValue ?? dataTop;
        return [bottom, top];
      }
      return [dataBottom, dataTop];
    };

    return [
      {
        yAxisId: 'left',
        fieldDbNames: lowerGroup.map(r => r.fieldDbName),
        domain: applyLower(lowerMin, lowerMax),
      },
      {
        yAxisId: 'right',
        fieldDbNames: upperGroup.map(r => r.fieldDbName),
        domain: applyUpper(upperMin, upperMax),
      },
    ];
  }, [fieldRanges, selectedFields, useDualYAxis, yAxisSide, yAxisScale, yAxisStartValue, yAxisEndValue]);

  const primaryYAxisId = yAxisGroups[0]?.yAxisId ?? 'left';

  const windDirectionYAxisId = useMemo(() => {
    if (!selectedFields.includes('wind_direction')) return primaryYAxisId;
    const group = yAxisGroups.find(g => g.fieldDbNames.includes('wind_direction'));
    return group?.yAxisId ?? primaryYAxisId;
  }, [yAxisGroups, selectedFields, primaryYAxisId]);

  // Generate chart series for selected fields
  const chartSeries = useMemo(() => {
    const series: React.ReactElement[] = [];
    
    // Helper to find which yAxisId a field should use
    const getYAxisIdForField = (fieldDbName: string): string => {
      const group = yAxisGroups.find(g => g.fieldDbNames.includes(fieldDbName));
      return group?.yAxisId ?? yAxisGroups[0]?.yAxisId ?? 'left';
    };
    
    selectedFields.forEach((fieldDbName, index) => {
      const field = availableFields.find(f => f.db_name === fieldDbName);
      const displayName = field?.display_name || fieldDbName;
      const yAxisId = getYAxisIdForField(fieldDbName);
      const color = FIELD_COLORS[index % FIELD_COLORS.length];

      if (chartType === 'line') {
        series.push(
          <Line
            key={fieldDbName}
            type="monotone"
            dataKey={fieldDbName}
            name={displayName}
            stroke={color}
            strokeWidth={2}
            dot={false}
            connectNulls={false}
            yAxisId={yAxisId}
            isAnimationActive={chartAnimationsEnabled}
          />
        );
      } else {
        series.push(
          <Bar
            key={fieldDbName}
            dataKey={fieldDbName}
            name={displayName}
            fill={color}
            yAxisId={yAxisId}
            isAnimationActive={chartAnimationsEnabled}
          />
        );
      }
    });
    
    return series;
  }, [selectedFields, chartType, availableFields, yAxisGroups, chartAnimationsEnabled]);

  // Generate session reference areas for hourly chart
  // Linear time: uses session start/end (ms). Sessions-only: spans ordinal indices that overlap each session.
  const sessionReferenceAreas = useMemo(() => {
    if (forecastType !== 'hourly' || sessions.length === 0 || chartData.length === 0) {
      return [];
    }

    if (useCollapsedSessionAxis) {
      return sessions.map((session, index) => {
        const sessionStartMs = new Date(session.start_time).getTime();
        const sessionEndMs = new Date(session.end_time).getTime();

        const extent = sessionIntervalToCollapsedXExtent(chartData, sessionStartMs, sessionEndMs);
        if (!extent) return null;

        const [x1, x2] = extent;

        return (
          <ReferenceArea
            key={`session-${index}`}
            x1={x1}
            x2={x2}
            yAxisId={primaryYAxisId}
            fill={isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)'}
            fillOpacity={1}
            stroke={isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.15)'}
            strokeWidth={1}
            ifOverflow="visible"
          >
            <Label
              value={session.session_name}
              position="insideTop"
              fill={isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)'}
              fontSize={11}
              fontWeight={500}
              offset={5}
            />
          </ReferenceArea>
        );
      }).filter(Boolean);
    }

    const firstMs = (chartData[0].timestampMs ?? new Date(chartData[0].timestamp).getTime()) as number;
    const lastMs = (chartData[chartData.length - 1].timestampMs ?? new Date(chartData[chartData.length - 1].timestamp).getTime()) as number;
    const chartEndMs = lastMs + ONE_HOUR_MS; // Last point represents start of hour; chart extends to end of that hour

    return sessions.map((session, index) => {
      const sessionStartMs = new Date(session.start_time).getTime();
      const sessionEndMs = new Date(session.end_time).getTime();

      // Only render if session overlaps the chart's time range
      if (sessionEndMs <= firstMs || sessionStartMs >= chartEndMs) {
        return null;
      }

      return (
        <ReferenceArea
          key={`session-${index}`}
          x1={sessionStartMs}
          x2={sessionEndMs}
          yAxisId={primaryYAxisId}
          fill={isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)'}
          fillOpacity={1}
          stroke={isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.15)'}
          strokeWidth={1}
          ifOverflow="visible"
        >
          <Label
            value={session.session_name}
            position="insideTop"
            fill={isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)'}
            fontSize={11}
            fontWeight={500}
            offset={5}
          />
        </ReferenceArea>
      );
    }).filter(Boolean);
  }, [forecastType, sessions, chartData, isDark, primaryYAxisId, useCollapsedSessionAxis]);

  // Compute the "NOW" reference line position (number for hourly, dateTimeLabel for daily)
  const nowRefValue = useMemo(() => {
    if (chartData.length === 0) return null;

    if (forecastType === 'hourly') {
      const nowMs = currentTime.getTime();

      if (useCollapsedSessionAxis) {
        for (let i = 0; i < chartData.length; i++) {
          const pt = chartData[i];
          const t = (pt.timestampMs ?? new Date(pt.timestamp).getTime()) as number;
          if (nowMs >= t && nowMs < t + ONE_HOUR_MS) {
            const ord = pt.collapsedHourOrdinal ?? i;
            return ord + (nowMs - t) / ONE_HOUR_MS;
          }
        }
        return null;
      }

      const firstMs = (chartData[0].timestampMs ?? new Date(chartData[0].timestamp).getTime()) as number;
      const lastPoint = chartData[chartData.length - 1];
      const lastMs = (lastPoint.timestampMs ?? new Date(lastPoint.timestamp).getTime()) as number;
      const chartEndMs = lastMs + ONE_HOUR_MS;
      // Only show NOW if it falls within the chart range
      if (nowMs < firstMs || nowMs >= chartEndMs) return null;
      return nowMs;
    } else {
      // For daily: match on the same calendar date
      const nowDateKey = getDateKeyInTimeZone(currentTime, timezone);
      const match = chartData.find(point => point.dateKey === nowDateKey);
      return match?.dateTimeLabel ?? null;
    }
  }, [chartData, currentTime, forecastType, timezone, useCollapsedSessionAxis]);

  // Get field options for select
  const fieldOptions = useMemo(() => {
    return availableFields
      .filter(field => {
        // Filter out non-numeric fields (like weather_code)
        // Allow wind_direction as it's numeric (degrees)
        if (field.db_name.includes('weather_code')) {
          return false;
        }
        // Exclude other direction fields but allow wind_direction
        if (field.db_name.includes('direction') && field.db_name !== 'wind_direction') {
          return false;
        }
        return true;
      })
      .map(field => {
        // Wind fields are converted from m/s to km/h for display
        const isWindField = field.db_name.includes('wind_speed') || field.db_name.includes('wind_gust');
        const displayUnit = isWindField ? 'km/h' : field.unit;
        return {
          value: field.db_name,
          label: `${field.display_name}${displayUnit ? ` (${displayUnit})` : ''}`,
        };
      });
  }, [availableFields]);

  const chartColors = {
    grid: isDark ? '#444' : '#ddd',
    text: isDark ? '#fff' : '#000',
  };

  const isCompact = variant === 'compact';
  const chartFontSizes = isCompact
    ? { axis: 9, legend: 10 }
    : { axis: 11, legend: 12 };

  const labelStyle: React.CSSProperties = {
    color: chartColors.text,
    fontWeight: 500,
    whiteSpace: 'nowrap',
    flexShrink: 0,
  };

  const chartControlsContent = (
    <>
      <div style={{ flex: 1, minWidth: 300, display: 'flex', alignItems: 'center', gap: 6 }}>
        <label style={{ ...labelStyle, fontSize: 12 }}>
          Weather Parameters (select multiple):
        </label>
        <Select
          mode="multiple"
          size="small"
          value={selectedFields}
          onChange={setSelectedFields}
          style={{ flex: 1, minWidth: 0, width: '100%' }}
          placeholder="Select weather parameters to plot"
          options={fieldOptions}
          maxTagCount="responsive"
        />
      </div>

      <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 6 }}>
        <label style={{ ...labelStyle, fontSize: 12 }}>Chart Type:</label>
        <Radio.Group size="small" value={chartType} onChange={(e) => setChartType(e.target.value)} buttonStyle="solid">
          <Radio.Button value="line">Line Chart</Radio.Button>
          <Radio.Button value="bar">Bar Chart</Radio.Button>
        </Radio.Group>
      </div>

      <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 6 }}>
        <label style={{ ...labelStyle, fontSize: 12 }}>Legend:</label>
        <Switch
          size="small"
          checked={showLegend}
          onChange={setShowLegend}
          checkedChildren="On"
          unCheckedChildren="Off"
        />
      </div>

      <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 6 }}>
        <label style={{ ...labelStyle, fontSize: 12 }}>Dual Y-Axis:</label>
        <Switch
          size="small"
          checked={useDualYAxis}
          onChange={setUseDualYAxis}
          checkedChildren="Dual"
          unCheckedChildren="Single"
          disabled={selectedFields.length < 2}
        />
      </div>

      <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 6 }}>
        <label style={{ ...labelStyle, fontSize: 12 }}>Y-Axis side:</label>
        <Radio.Group size="small" value={yAxisSide} onChange={(e) => setYAxisSide(e.target.value)} buttonStyle="solid" disabled={useDualYAxis}>
          <Radio.Button value="left">Left</Radio.Button>
          <Radio.Button value="right">Right</Radio.Button>
        </Radio.Group>
      </div>

      <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <label style={{ ...labelStyle, fontSize: 12 }}>Y-Axis scale:</label>
        <Radio.Group size="small" value={yAxisScale} onChange={(e) => setYAxisScale(e.target.value)} buttonStyle="solid">
          <Radio.Button value="auto">Auto</Radio.Button>
          <Radio.Button value="custom">Custom</Radio.Button>
        </Radio.Group>
        {yAxisScale === 'custom' && (
          <>
            <span style={{ color: chartColors.text, fontSize: 11 }}>Start at</span>
            <InputNumber value={yAxisStartValue} onChange={(v) => setYAxisStartValue(v ?? 0)} size="small" style={{ width: 64 }} title="Y-axis minimum" />
            <span style={{ color: chartColors.text, fontSize: 11 }}>End at</span>
            <InputNumber value={yAxisEndValue ?? undefined} onChange={(v) => setYAxisEndValue(v ?? null)} size="small" style={{ width: 64 }} placeholder="auto" title="Y-axis maximum (empty = auto from data)" />
          </>
        )}
      </div>
    </>
  );

  const chartControlsContentCompact = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24, width: '100%' }}>
      {widgetConfig && onWidgetConfigChange && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <label style={{ ...labelStyle, fontSize: 12 }}>Forecast Type</label>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Auto picks the most recently issued forecast at the timeline position.
            </Typography.Text>
            <Segmented
              block
              value={widgetConfig.forecastMode}
              options={[
                { label: 'Auto', value: 'auto' },
                { label: 'Daily', value: 'daily' },
                { label: 'Hourly', value: 'hourly' },
              ]}
              onChange={(val) => onWidgetConfigChange({ forecastMode: val as ForecastChartWidgetMode })}
            />
          </div>

          {(widgetConfig.forecastMode === 'auto' || widgetConfig.forecastMode === 'hourly') && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <label style={{ ...labelStyle, fontSize: 12 }}>Session Filter (Hourly)</label>
              {widgetConfig.forecastMode === 'auto' && (
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  Applies when auto selects an hourly forecast.
                </Typography.Text>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Switch
                  checked={widgetConfig.filterBySession}
                  onChange={(checked) => onWidgetConfigChange({ filterBySession: checked })}
                  size="small"
                />
                <span style={{ color: chartColors.text, fontSize: 12 }}>Sessions only</span>
              </div>
              {widgetConfig.filterBySession && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ color: chartColors.text, fontSize: 12 }}>Hours before session:</span>
                  <InputNumber
                    min={0}
                    max={12}
                    value={widgetConfig.hoursBeforeSession}
                    onChange={(val) => onWidgetConfigChange({ hoursBeforeSession: val ?? 2 })}
                    size="small"
                    style={{ width: 72 }}
                  />
                </div>
              )}
            </div>
          )}

          <Divider style={{ margin: 0 }} />
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <label style={{ ...labelStyle, fontSize: 12 }}>Weather Parameters</label>
        <Select
          mode="multiple"
          value={selectedFields}
          onChange={setSelectedFields}
          style={{ width: '100%' }}
          placeholder="Select weather parameters to plot"
          options={fieldOptions}
          maxTagCount="responsive"
          size="small"
        />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <label style={{ ...labelStyle, fontSize: 12 }}>Chart Type</label>
        <Radio.Group value={chartType} onChange={(e) => setChartType(e.target.value)} buttonStyle="solid" size="small">
          <Radio.Button value="line">Line</Radio.Button>
          <Radio.Button value="bar">Bar</Radio.Button>
        </Radio.Group>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <label style={{ ...labelStyle, fontSize: 12 }}>Display</label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px 24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ ...labelStyle, fontSize: 11, fontWeight: 400 }}>Legend</span>
            <Switch checked={showLegend} onChange={setShowLegend} size="small" checkedChildren="On" unCheckedChildren="Off" />
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <label style={{ ...labelStyle, fontSize: 12 }}>Y-Axis</label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px 20px', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ ...labelStyle, fontSize: 11, fontWeight: 400 }}>Dual Y-Axis</span>
            <Switch
              checked={useDualYAxis}
              onChange={setUseDualYAxis}
              size="small"
              disabled={selectedFields.length < 2}
              checkedChildren="Dual"
              unCheckedChildren="Single"
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ ...labelStyle, fontSize: 11, fontWeight: 400 }}>Side</span>
            <Radio.Group value={yAxisSide} onChange={(e) => setYAxisSide(e.target.value)} buttonStyle="solid" size="small" disabled={useDualYAxis}>
              <Radio.Button value="left">Left</Radio.Button>
              <Radio.Button value="right">Right</Radio.Button>
            </Radio.Group>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ ...labelStyle, fontSize: 11, fontWeight: 400 }}>Scale</span>
            <Radio.Group value={yAxisScale} onChange={(e) => setYAxisScale(e.target.value)} buttonStyle="solid" size="small">
              <Radio.Button value="auto">Auto</Radio.Button>
              <Radio.Button value="custom">Custom</Radio.Button>
            </Radio.Group>
            {yAxisScale === 'custom' && (
              <>
                <span style={{ ...labelStyle, fontSize: 11, fontWeight: 400 }}>Start at</span>
                <InputNumber value={yAxisStartValue} onChange={(v) => setYAxisStartValue(v ?? 0)} size="small" style={{ width: 72 }} title="Y-axis minimum" />
                <span style={{ ...labelStyle, fontSize: 11, fontWeight: 400 }}>End at</span>
                <InputNumber value={yAxisEndValue ?? undefined} onChange={(v) => setYAxisEndValue(v ?? null)} size="small" style={{ width: 72 }} placeholder="auto" title="Y-axis maximum" />
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderChartBody = () => (
    <>
      {selectedFields.length > 0 && chartData.length > 0 && !hideFullScreen && (
        <Button
          type="text"
          icon={<FullscreenOutlined />}
          onClick={() => setIsFullScreen(true)}
          title="Full screen chart"
          size="small"
          style={{ color: chartColors.text, position: 'absolute', top: 4, right: 4, zIndex: 1 }}
        />
      )}
      {validForecasts.length === 0 ? (
        <div style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: chartColors.text,
          padding: embeddedInWidget ? '8px 0' : (isCompact ? '16px 0' : '40px 0'),
          textAlign: 'center',
          fontSize: isCompact ? 12 : 14,
        }}>
          {emptyMessage ?? 'No forecast data available'}
        </div>
      ) : selectedFields.length === 0 ? (
        <div style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: chartColors.text,
          padding: embeddedInWidget ? '8px 0' : (isCompact ? '16px 0' : '40px 0'),
        }}>
          Please select at least one weather parameter to display
        </div>
      ) : chartData.length === 0 ? (
        <div style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: chartColors.text,
          padding: embeddedInWidget ? '8px 0' : (isCompact ? '16px 0' : '40px 0'),
        }}>
          No data available for the selected parameters
        </div>
      ) : (
        <div ref={chartContainerRef} style={{ flex: 1, minHeight: 0, position: 'relative' }}>
          {renderChartContent()}
        </div>
      )}
    </>
  );

  const renderChartContent = (height?: number) => {
    const hasWindDirection = selectedFields.includes('wind_direction');
    const ChartComponent = hasWindDirection ? ComposedChart : (chartType === 'line' ? LineChart : BarChart);
    const hasRightAxis = yAxisGroups.some(g => g.yAxisId === 'right');
    const hasLeftAxis = yAxisGroups.some(g => g.yAxisId === 'left');

    const totalHeight = height ?? chartHeight;

    const chartMargins = isCompact
      ? {
          top: 4,
          right: hasRightAxis ? 36 : 4,
          left: hasLeftAxis ? 0 : 0,
          bottom: 2,
        }
      : {
          top: 10,
          right: hasRightAxis ? 80 : 30,
          left: hasLeftAxis ? 20 : 10,
          bottom: showLegend ? 30 : 10,
        };

    const xAxisHeight = isCompact ? 36 : 50;
    const yAxisWidth = isCompact ? 30 : undefined;

    const chartInner = (
      <ChartComponent
        data={chartData}
        margin={chartMargins}
      >
        <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
        {sessionReferenceAreas}
        {nowRefValue != null && (
          <ReferenceLine
            x={nowRefValue}
            yAxisId={primaryYAxisId}
            stroke="rgba(255, 77, 79, 0.9)"
            strokeWidth={2}
            strokeDasharray="4 4"
            ifOverflow="visible"
          >
            <Label
              value="NOW"
              position="top"
              fill="rgba(255, 77, 79, 0.9)"
              fontSize={isCompact ? 9 : 10}
              fontWeight="bold"
              offset={8}
            />
          </ReferenceLine>
        )}
        <XAxis
          dataKey={
            forecastType === 'hourly'
              ? (useCollapsedSessionAxis ? 'collapsedX' : 'timestampMs')
              : 'dateTimeLabel'
          }
          type={forecastType === 'hourly' ? 'number' : 'category'}
          stroke={chartColors.text}
          style={{ fill: chartColors.text }}
          angle={0}
          height={xAxisHeight}
          tick={
            forecastType === 'hourly' && useCollapsedSessionAxis
              ? (props) => {
                  const xVal = Number(props.payload?.value);
                  const pt = chartData.find((p) => p.collapsedX === xVal);
                  if (!pt) return <g />;
                  const ms = (pt.timestampMs ?? new Date(pt.timestamp).getTime()) as number;
                  return (
                    <CustomXAxisTick
                      {...props}
                      payload={{ value: ms }}
                      textColor={chartColors.text}
                      timezone={timezone}
                      fontSize={chartFontSizes.axis}
                    />
                  );
                }
              : (props) => (
                  <CustomXAxisTick
                    {...props}
                    textColor={chartColors.text}
                    timezone={timezone}
                    fontSize={chartFontSizes.axis}
                  />
                )
          }
          domain={
            forecastType === 'hourly' && chartData.length > 0
              ? (() => {
                  if (useCollapsedSessionAxis) {
                    const n = chartData.length;
                    return [0, Math.max(n, 1)];
                  }
                  const first = (chartData[0].timestampMs ?? new Date(chartData[0].timestamp).getTime()) as number;
                  const last = (chartData[chartData.length - 1].timestampMs ?? new Date(chartData[chartData.length - 1].timestamp).getTime()) as number;
                  return [first, last + 60 * 60 * 1000];
                })()
              : undefined
          }
          ticks={forecastType === 'hourly' && useCollapsedSessionAxis ? collapsedAxisTicks : undefined}
        />
        {yAxisGroups.map((group) => {
          const fieldLabels = group.fieldDbNames.map(fieldDbName => {
            const field = availableFields.find(f => f.db_name === fieldDbName);
            return field?.display_name || fieldDbName;
          });

          return (
            <YAxis
              key={group.yAxisId}
              yAxisId={group.yAxisId}
              orientation={group.yAxisId === 'left' ? 'left' : 'right'}
              stroke={chartColors.text}
              style={{ fill: chartColors.text, fontSize: chartFontSizes.axis }}
              width={yAxisWidth}
              domain={group.domain}
              allowDataOverflow={yAxisScale === 'custom'}
              label={{
                value: yAxisGroups.length > 1 ? fieldLabels.join(', ') : '',
                angle: -90,
                position: 'center',
                dx: group.yAxisId === 'left' ? (isCompact ? -12 : -20) : (isCompact ? 12 : 20),
                style: {
                  fill: chartColors.text,
                  fontSize: Math.max(8, chartFontSizes.axis - (group.fieldDbNames.length - 1)),
                  fontWeight: 500,
                },
              }}
            />
          );
        })}
        <Tooltip
          content={({ active, payload, label }) => {
            if (!active || !payload?.length) return null;
            const pt = payload[0]?.payload as ChartDataPoint | undefined;
            const labelText =
              forecastType === 'hourly' && pt?.timestampMs != null
                ? new Date(pt.timestampMs).toLocaleString('en-US', {
                    month: 'short',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false,
                    timeZone: timezone,
                  })
                : typeof label === 'string'
                  ? label
                  : String(label ?? '');

            const rows = payload.filter((entry) => {
              const dk = entry.dataKey as string | undefined;
              if (dk == null || dk === '') return false;
              if (TOOLTIP_INTERNAL_DATA_KEYS.has(dk)) return false;
              return selectedFields.includes(dk);
            });
            if (rows.length === 0) return null;

            return (
              <div
                style={{
                  backgroundColor: isDark ? '#1f1f1f' : '#fff',
                  border: `1px solid ${chartColors.grid}`,
                  color: chartColors.text,
                  padding: '8px 12px',
                  borderRadius: 4,
                  fontSize: chartFontSizes.axis,
                }}
              >
                <div style={{ marginBottom: 8, fontWeight: 500 }}>{labelText}</div>
                {rows.map((entry, i) => {
                  const v = entry.value;
                  const display =
                    typeof v === 'number' && !Number.isNaN(v)
                      ? v.toFixed(1)
                      : String(v ?? '');
                  return (
                    <div
                      key={`${String(entry.dataKey)}-${i}`}
                      style={{ color: entry.color ?? chartColors.text }}
                    >
                      {entry.name}: {display}
                    </div>
                  );
                })}
              </div>
            );
          }}
        />
        {showLegend && !isCompact && (
          <Legend
            wrapperStyle={{ color: chartColors.text, paddingTop: 0 }}
          />
        )}
        {chartSeries}
        {hasWindDirection && (
          <Scatter
            dataKey="windArrowY"
            yAxisId={windDirectionYAxisId}
            shape={<WindArrow />}
            legendType="none"
            isAnimationActive={chartAnimationsEnabled}
          />
        )}
      </ChartComponent>
    );

    const legendElement = showLegend ? (
      <div style={{
        flexShrink: 0,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: isCompact ? 10 : 16,
        padding: isCompact ? '0 0 2px' : undefined,
      }}>
        {selectedFields.map((fieldDbName, index) => {
          const field = availableFields.find(f => f.db_name === fieldDbName);
          const displayName = field?.display_name || fieldDbName;
          const color = FIELD_COLORS[index % FIELD_COLORS.length];
          return (
            <div key={fieldDbName} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{
                width: 20,
                height: 3,
                backgroundColor: color,
                borderRadius: 2,
              }} />
              <span style={{ color: chartColors.text, fontSize: chartFontSizes.legend }}>{displayName}</span>
            </div>
          );
        })}
      </div>
    ) : null;

    const hasLegend = showLegend && selectedFields.length > 0;
    if (totalHeight <= 0) {
      return null;
    }

    const compactLegendReserve = hasLegend ? 26 : 0;
    const compactPlotHeight = Math.max(60, totalHeight - compactLegendReserve);

    if (isCompact) {
      return (
        <div style={{ width: '100%', height: totalHeight, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <div style={{ height: compactPlotHeight, minHeight: 0, position: 'relative' }}>
            <ResponsiveContainer width="100%" height={compactPlotHeight} debounce={250}>
              {chartInner}
            </ResponsiveContainer>
          </div>
          {legendElement}
        </div>
      );
    }

    return (
      <div style={{ width: '100%', height: totalHeight, position: 'relative', overflow: 'hidden' }}>
        <ResponsiveContainer width="100%" height={totalHeight} debounce={250}>
          {chartInner}
        </ResponsiveContainer>
      </div>
    );
  };

  if (validForecasts.length === 0 && variant !== 'compact') {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '40px 0', color: chartColors.text }}>
          {emptyMessage ?? 'No forecast data available'}
        </div>
      </Card>
    );
  }

  return (
    <div style={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      padding: embeddedInWidget ? 0 : (isCompact ? '4px' : '0'),
      minHeight: 0,
      overflow: 'hidden',
    }}>
      {/* Inline controls — full variant only */}
      {variant === 'full' && (
        <Card
          size="small"
          style={{
            marginBottom: 0,
            backgroundColor: isDark ? '#1f1f1f' : '#fff',
            flexShrink: 0,
            fontSize: 12,
            borderRadius: 0,
          }}
          styles={{ body: { padding: '4px 10px' } }}
        >
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px 12px' }}>
            {chartControlsContent}
          </div>
        </Card>
      )}

      {/* Chart */}
      {(() => {
        const chartBodyStyle: React.CSSProperties = {
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          position: 'relative',
          overflow: 'hidden',
          ...(embeddedInWidget
            ? { padding: '6px 10px 8px' }
            : isCompact
              ? { padding: '4px 8px 6px' }
              : {}),
        };

        const chartBody = renderChartBody();

        if (embeddedInWidget) {
          return (
            <div style={chartBodyStyle}>
              {chartBody}
            </div>
          );
        }

        return (
          <Card
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              minHeight: minChartHeight,
              backgroundColor: isDark ? '#1f1f1f' : '#fff',
              position: 'relative',
              borderRadius: 0,
            }}
            styles={{
              header: { display: 'none' },
              body: chartBodyStyle,
            }}
          >
            {chartBody}
          </Card>
        );
      })()}

      {/* Settings drawer for compact variant */}
      {variant === 'compact' && (
        <Drawer
          title="Chart Settings"
          placement="right"
          onClose={() => setIsSettingsDrawerOpen(false)}
          open={isSettingsDrawerOpen}
          size={Math.min(520, typeof window !== 'undefined' ? window.innerWidth * 0.9 : 520)}
          styles={{
            body: { backgroundColor: isDark ? '#1f1f1f' : '#fff' },
            header: { backgroundColor: isDark ? '#1f1f1f' : '#fff', color: chartColors.text },
          }}
        >
          <Card style={{ backgroundColor: 'transparent', border: 'none', boxShadow: 'none' }}>
            {chartControlsContentCompact}
          </Card>
        </Drawer>
      )}

      {/* Fullscreen chart modal */}
      <Modal
        open={isFullScreen}
        onCancel={() => setIsFullScreen(false)}
        footer={[
          <Button key="close" onClick={() => setIsFullScreen(false)}>
            Close
          </Button>,
        ]}
        width="100%"
        styles={{
          body: {
            height: 'calc(100vh - 160px)',
            maxHeight: 'calc(100vh - 160px)',
            padding: 16,
            overflow: 'hidden',
            backgroundColor: isDark ? '#1f1f1f' : '#fff',
          },
          wrapper: { maxWidth: '100vw' },
        }}
        style={{ top: 24, paddingBottom: 0 }}
        title="Forecast Chart - Full Screen"
      >
        {chartData.length > 0 && renderChartContent(fullscreenChartHeight)}
      </Modal>
    </div>
  );
};
